import { http } from '@/api/http';
import type { Outcome } from '@/api/outcome';

export interface Puzzle {
  slug: string;
  fen: string;
  side_to_move: 'white' | 'black';
  difficulty: number;
  theme: string;
  secondary_themes: string[];
  solution: string[];
  explanation: string;
  source: { kind: string; ref: string };
  /**
   * Chantier 2 FFE (2026-05-17) — sous-classification optionnelle par mat
   * nommé du livre FFE Cavalier (Boden, Arabes, Couloir, Étouffée, etc.).
   * Présent uniquement sur ~0,5% des puzzles (8/1518 à ce jour), s'étendra
   * en MVP4.2 (Concept Graph + re-seed CSV Lichess complet).
   * Cf. `docs/pedagogie/audit-tactics-dojo-mats-ffe.md`.
   */
  pattern_tag?: string;
}

export interface PuzzleListResponse {
  count: number;
  puzzles: Puzzle[];
}

export interface ThemesResponse {
  count: number;
  themes: Array<{ theme: string; n: number }>;
}

/**
 * Modes du Tactics Dojo (MVP2.6).
 *
 * `tactic_drill` / `tactic_drill_random` : drill standard sans pression
 * (MVP2.5, déjà livré).
 *
 * `tactic_drill_time_pressed` : 30 s/puzzle, force la vitesse pour fermer
 * le gap Lichess Puzzle 2144 vs Blitz 1949 de Léon (axe pédagogique #1).
 * Cf. `TimePressedTimer.tsx` + PRODUCT.md §4.2.
 *
 * `tactic_drill_in_context` : la position arrive après ~10 coups d'ouverture
 * jouée par Léon (animation pré-drill) — habitue le passage middlegame →
 * tactique. V0 : mapping statique thème → ouverture (cf. `inContextMapping.ts`).
 *
 * `tactic_drill_misere` : Léon décide « il y a une tactique » vs « non, calme
 * jeu » avant de jouer. Force le calcul de réfutation. Cf. `MisereDecision.tsx`.
 */
export type TacticMode =
  | 'tactic_drill'
  | 'tactic_drill_random'
  | 'tactic_drill_time_pressed'
  | 'tactic_drill_in_context'
  | 'tactic_drill_misere';

export interface TacticAttemptPayload {
  module: 'tactic';
  slug: string;
  kind: 'tactic_position';
  fen: string;
  side_to_move: 'white' | 'black';
  mode: TacticMode;
  result: 'success' | 'partial' | 'fail';
  played_move?: string;
  expected_move?: string;
  time_spent_ms: number;
  source_ref?: string;
  /** MVP2.4 T9.1 — 5 états (cf. @/api/outcome). Optionnel pour rétro-compat. */
  outcome?: Outcome;
  /**
   * MVP2.6 Gap 1 — `true` si le puzzle a été résolu sous contrainte temporelle
   * (mode `tactic_drill_time_pressed`, 30 s plafond) OU si le timer a expiré
   * (`result='fail'` avec `time_spent_ms=30000`). Le backend persiste la valeur
   * dans `training_attempts.time_pressure` (migration 020) pour permettre
   * l'analyse parent : « est-ce que Léon échoue surtout sous pression ? ».
   */
  time_pressure?: boolean;
}

export interface AttemptResponse {
  attempt_id: number;
  item_id: number;
  card_created: boolean;
  card_id: number | null;
}

/**
 * Payload du 404 « plus de puzzles » retourné par le backend (T9 MVP2.5)
 * quand `offset >= count(theme)`. Permet au frontend d'afficher le message
 * kid-friendly « Tu as fini tous les puzzles de ce thème… ».
 */
export interface NoMorePuzzlesError {
  error: 'no_more_puzzles';
  theme: string;
  total: number;
}

export const tacticsApi = {
  themes: () => http.get<ThemesResponse>('/api/v1/content/tactics/themes'),
  list: (params: { theme?: string; ratingMin?: number; ratingMax?: number; limit?: number; offset?: number; random?: boolean } = {}) => {
    const qs = new URLSearchParams();
    if (params.theme) qs.set('theme', params.theme);
    if (params.ratingMin) qs.set('rating_min', String(params.ratingMin));
    if (params.ratingMax) qs.set('rating_max', String(params.ratingMax));
    qs.set('limit', String(params.limit ?? 10));
    if (params.offset !== undefined && params.offset > 0) qs.set('offset', String(params.offset));
    if (params.random) qs.set('random', '1');
    return http.get<PuzzleListResponse>(`/api/v1/content/tactics?${qs.toString()}`);
  },
  postAttempt: (payload: TacticAttemptPayload) =>
    http.post<AttemptResponse>('/api/v1/training/attempts', payload),
};
